import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  Stack,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Badge,
} from "@mui/material";
import {
  UploadFile as UploadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FolderShared as ResourceIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../api";
import {
  resourceChipFor as chipFor,
  RESOURCE_SOURCE_LABEL as SOURCE_LABEL,
} from "./tutorStatusChips";
import useGroupTaxonomy from "./useGroupTaxonomy";
import TutorContentFilters, { statusParamsFor } from "./components/TutorContentFilters";

const MAX_MB = 50;

const EMPTY_FORM = {
  course_plan_id: "",
  plan_week_id: "",
  subject_id: "",
  topic_id: "",
  subtopic_id: "",
  title: "",
  description: "",
};

const EMPTY_FILTERS = {
  course_plan_id: "",
  plan_week_id: "",
  subject_id: "",
  topic_id: "",
  subtopic_id: "",
};

/**
 * Tutor content upload, plus the lead tutor's review queue.
 *
 * Two stages: a tutor assistant's upload waits for this group's lead tutor, then for an admin.
 * Nothing here makes a file visible to a student — only the admin's verdict does, and the UI says
 * so at upload time so a tutor never assumes their file is live.
 */
export default function TutorResources() {
  const [tab, setTab] = useState(0);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [queue, setQueue] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Lead-tutor verdict in flight: { row, status }.
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewing, setReviewing] = useState(false);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);

  // Filter bar state. Every one of these is a real query parameter on tutor/resources, so the
  // narrowing happens on the server rather than over rows already fetched.
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [statusFilter, setStatusFilter] = useState("all");

  // Two independent cascades: one behind the filter bar, one inside the upload dialog.
  const filterTax = useGroupTaxonomy(filters.course_plan_id, filters.subject_id, filters.topic_id);
  const formTax = useGroupTaxonomy(form.course_plan_id, form.subject_id, form.topic_id);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { ...statusParamsFor(statusFilter) };
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v != null) params[k] = v;
      });

      // allSettled, not all: a tutor who leads no group gets nothing useful from the queue call,
      // and that must not discard the resource list the way a rejected Promise.all would.
      const [res, grp, q] = await Promise.allSettled([
        api.get("tutor/resources", { params }),
        api.get("tutor/groups"),
        api.get("tutor/resources/review-queue"),
      ]);

      if (res.status === "rejected") throw res.reason;

      setRows(Array.isArray(res.value.data?.data) ? res.value.data.data : []);
      setGroups(
        grp.status === "fulfilled" && Array.isArray(grp.value.data?.data) ? grp.value.data.data : []
      );
      setQueue(
        q.status === "fulfilled" && Array.isArray(q.value.data?.data) ? q.value.data.data : []
      );
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load your resources.");
    } finally {
      setLoading(false);
    }
  }, [filters, statusFilter]);

  /** The lead tutor's verdict. Approving only forwards to an admin — it does not publish. */
  const submitReview = async () => {
    if (!reviewTarget) return;
    setReviewing(true);
    try {
      const res = await api.post(`tutor/resources/${reviewTarget.row.id}/lead-review`, {
        status: reviewTarget.status,
        notes: reviewNotes || undefined,
      });
      toast.success(res.data?.message || "Review saved.");
      setReviewTarget(null);
      setReviewNotes("");
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.data?.error || e?.response?.data?.message || "Could not save the review."
      );
    } finally {
      setReviewing(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  // Default to the week in progress once the group's weeks arrive — that is nearly always the
  // intent, and picking the group already cleared any earlier choice.
  useEffect(() => {
    const current = formTax.weeks.find((w) => w.is_current);
    if (current) setForm((f) => (f.plan_week_id ? f : { ...f, plan_week_id: current.id }));
  }, [formTax.weeks]);

  const hasActiveFilters =
    statusFilter !== "all" || Object.values(filters).some((v) => v !== "" && v != null);

  // "Uploaded by" stays client-side: the server has no `source` param, and the list is one
  // tutor's own groups, so it is small enough that a second round trip would buy nothing.
  const visibleRows = rows.filter((r) => sourceFilter === "all" || r.source === sourceFilter);

  /** Changing a filter drops everything below it, the way the server scopes these lists. */
  const changeFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "course_plan_id" && {
        plan_week_id: "",
        subject_id: "",
        topic_id: "",
        subtopic_id: "",
      }),
      ...(key === "subject_id" && { topic_id: "", subtopic_id: "" }),
      ...(key === "topic_id" && { subtopic_id: "" }),
    }));
  };

  const submit = async () => {
    // Subject is required by the API too: content is filed subject-wise, and a file with no subject
    // never appears in a subject view.
    if (!form.course_plan_id || !form.subject_id || !form.title || !file) {
      toast.error("Group, subject, title and file are required.");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File must be ${MAX_MB}MB or smaller.`);
      return;
    }

    const body = new FormData();
    body.append("course_plan_id", form.course_plan_id);
    if (form.plan_week_id) body.append("plan_week_id", form.plan_week_id);
    if (form.subject_id) body.append("subject_id", form.subject_id);
    if (form.topic_id) body.append("topic_id", form.topic_id);
    if (form.subtopic_id) body.append("subtopic_id", form.subtopic_id);
    body.append("title", form.title);
    if (form.description) body.append("description", form.description);
    body.append("file", file);

    setSaving(true);
    try {
      const res = await api.post("tutor/resources", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data?.message || "Uploaded — awaiting admin approval.");
      setOpen(false);
      setForm(EMPTY_FORM);
      setFile(null);
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.errors?.file?.[0] ||
          e?.response?.data?.message ||
          "Upload failed."
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`tutor/resources/${id}`);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not delete.");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ResourceIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            My Content
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setOpen(true)}
            disabled={groups.length === 0}
          >
            Upload
          </Button>
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        PDF, PowerPoint or Word files for your groups. Your group's lead tutor reviews each upload,
        then an admin — students see it only after both. Filter by <strong>Approved</strong> or{" "}
        <strong>Not approved</strong> below to see where the admin has got to.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!loading && groups.length === 0 && (
        <Alert severity="info">You need to be assigned to a group before you can upload content.</Alert>
      )}

      {/* The review tab only exists for a lead tutor with something waiting — an assistant, or a
          lead with an empty queue, never sees an empty tab they cannot act on. */}
      {queue.length > 0 && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="My uploads" />
          <Tab
            label={
              <Badge color="warning" badgeContent={queue.length} sx={{ pr: 1.5 }}>
                Awaiting my approval
              </Badge>
            }
          />
        </Tabs>
      )}

      {tab === 1 && queue.length > 0 && (
        <Stack spacing={2}>
          <Alert severity="info">
            Uploads from your group's tutor assistants. Approving sends the file to an admin for
            final review — it does not publish it to students.
          </Alert>
          {queue.map((r) => (
            <Card key={r.id} variant="outlined" sx={{ borderRadius: 2, borderLeft: "4px solid #ed6c02" }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
                      {r.group?.name && <Chip size="small" variant="outlined" label={r.group.name} />}
                      {r.subject?.name && <Chip size="small" variant="outlined" label={r.subject.name} />}
                      {r.topic?.name && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={r.subtopic?.name ? `${r.topic.name} › ${r.subtopic.name}` : r.topic.name}
                        />
                      )}
                      {r.week?.week_number != null && (
                        <Chip size="small" variant="outlined" label={r.week.label || `Week ${r.week.week_number}`} />
                      )}
                    </Stack>
                    <Typography variant="h6" fontWeight={700}>
                      {r.title}
                    </Typography>
                    {r.description && (
                      <Typography variant="body2" color="text.secondary">
                        {r.description}
                      </Typography>
                    )}
                    {r.uploaded_by && (
                      <Typography variant="caption" color="text.secondary">
                        Uploaded by {r.uploaded_by}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {r.file_url && (
                      <Tooltip title="Download">
                        <IconButton size="small" component="a" href={r.file_url} target="_blank" rel="noopener noreferrer">
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setReviewNotes("");
                        setReviewTarget({ row: r, status: "rejected" });
                      }}
                    >
                      Reject
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        setReviewNotes("");
                        setReviewTarget({ row: r, status: "approved" });
                      }}
                    >
                      Approve
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={!!reviewTarget} onClose={() => !reviewing && setReviewTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {reviewTarget?.status === "approved" ? "Approve content" : "Reject content"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {reviewTarget?.status === "approved"
              ? `"${reviewTarget?.row?.title}" will be sent to an admin for final review. Students still cannot see it.`
              : `"${reviewTarget?.row?.title}" will be rejected and the uploader told. It goes no further.`}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes for the uploader (optional)"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewTarget(null)} disabled={reviewing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={reviewTarget?.status === "approved" ? "primary" : "error"}
            onClick={submitReview}
            disabled={reviewing}
          >
            {reviewing ? "Saving…" : reviewTarget?.status === "approved" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {tab !== 1 && groups.length > 0 && (
        <>
          <TutorContentFilters
            filters={filters}
            onChange={changeFilter}
            status={statusFilter}
            onStatusChange={setStatusFilter}
            groups={groups}
            weeks={filterTax.weeks}
            subjects={filterTax.subjects}
            topics={filterTax.topics}
            subtopics={filterTax.subtopics}
            disabled={loading}
            onReset={() => {
              setFilters(EMPTY_FILTERS);
              setStatusFilter("all");
              setSourceFilter("all");
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
            <InputLabel>Uploaded by</InputLabel>
            <Select
              value={sourceFilter}
              label="Uploaded by"
              onChange={(e) => setSourceFilter(e.target.value)}
            >
              <MenuItem value="all">Everyone</MenuItem>
              <MenuItem value="admin">Admin (course material)</MenuItem>
              <MenuItem value="tutor">Lead tutor</MenuItem>
              <MenuItem value="assistant">Tutor assistant</MenuItem>
            </Select>
          </FormControl>
        </>
      )}

      {tab === 1 ? null : loading ? (
        <Stack spacing={2}>
          {[1, 2].map((n) => (
            <Skeleton key={n} variant="rounded" height={110} />
          ))}
        </Stack>
      ) : (
        <Stack spacing={2}>
          {rows.length === 0 && groups.length > 0 && (
            <Alert severity="info">
              {hasActiveFilters
                ? "No content matches these filters."
                : "You have not uploaded anything yet."}
            </Alert>
          )}
          {rows.length > 0 && visibleRows.length === 0 && (
            <Alert severity="info">
              No content from that uploader matches these filters.
            </Alert>
          )}
          {visibleRows.map((r) => {
            const chip = chipFor(r);
            return (
              <Card key={r.id} variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${chip.color}` }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={chip.label}
                          sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 600 }}
                        />
                        {SOURCE_LABEL[r.source] && (
                          <Chip
                            size="small"
                            label={SOURCE_LABEL[r.source].label}
                            sx={{
                              bgcolor: SOURCE_LABEL[r.source].bg,
                              color: SOURCE_LABEL[r.source].color,
                              fontWeight: 700,
                            }}
                          />
                        )}
                        {r.group?.name && <Chip size="small" variant="outlined" label={r.group.name} />}
                        {r.subject?.name && <Chip size="small" variant="outlined" label={r.subject.name} />}
                        {r.topic?.name && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={r.subtopic?.name ? `${r.topic.name} › ${r.subtopic.name}` : r.topic.name}
                          />
                        )}
                        {r.week?.week_number != null && (
                          <Chip size="small" variant="outlined" label={r.week.label || `Week ${r.week.week_number}`} />
                        )}
                      </Stack>
                      <Typography variant="h6" fontWeight={700}>
                        {r.title}
                      </Typography>
                      {r.description && (
                        <Typography variant="body2" color="text.secondary">
                          {r.description}
                        </Typography>
                      )}
                      {/* A verdict with no reason leaves the uploader nothing to act on, and the
                          note is already in the payload — it was simply never rendered. */}
                      {r.lead_status === "rejected" && r.lead_notes && (
                        <Alert severity="warning" sx={{ mt: 1, py: 0.25 }}>
                          <strong>Lead tutor:</strong> {r.lead_notes}
                        </Alert>
                      )}
                      {r.stage === "rejected" && (
                        <Alert severity="error" sx={{ mt: 1, py: 0.25 }}>
                          An admin did not approve this, so students cannot see it. Ask your admin
                          for the reason, then upload a corrected version.
                        </Alert>
                      )}
                      {r.published_at && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Approved by admin {new Date(r.published_at).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      {r.file_url && (
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            component="a"
                            href={r.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Only the uploader may delete; the server refuses anything else, so the
                          action is hidden rather than offered and rejected. */}
                      {r.can_delete !== false && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => remove(r.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload content</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Students cannot see this until an admin approves it.
          </Alert>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Group</InputLabel>
              <Select
                value={form.course_plan_id}
                label="Group"
                onChange={(e) =>
                  // Weeks, subjects and the topic tree all belong to the group — reset the lot.
                  setForm({
                    ...form,
                    course_plan_id: e.target.value,
                    plan_week_id: "",
                    subject_id: "",
                    topic_id: "",
                    subtopic_id: "",
                  })
                }
              >
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                    {g.course?.name ? ` — ${g.course.name}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth disabled={!form.course_plan_id}>
              <InputLabel>Week</InputLabel>
              <Select
                value={form.plan_week_id}
                label="Week"
                onChange={(e) => setForm({ ...form, plan_week_id: e.target.value })}
              >
                <MenuItem value="">
                  <em>Whole course (not week-specific)</em>
                </MenuItem>
                {formTax.weeks.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    Week {w.week_number}
                    {w.start_date ? ` · ${w.start_date}` : ""}
                    {w.is_current ? " (current)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              fullWidth
              required
              disabled={!form.course_plan_id}
              error={Boolean(form.course_plan_id) && !form.subject_id}
            >
              <InputLabel>Subject</InputLabel>
              <Select
                value={form.subject_id}
                label="Subject"
                onChange={(e) =>
                  setForm({ ...form, subject_id: e.target.value, topic_id: "", subtopic_id: "" })
                }
              >
                {formTax.subjects.map((s) => (
                  <MenuItem key={s.subject_id} value={s.subject_id}>
                    {s.subject_name}
                  </MenuItem>
                ))}
              </Select>
              {!!form.course_plan_id && formTax.subjects.length === 0 && (
                <FormHelperText>No subjects have content for this group's year yet.</FormHelperText>
              )}
            </FormControl>

            <FormControl size="small" fullWidth disabled={!form.subject_id}>
              <InputLabel>Topic</InputLabel>
              <Select
                value={form.topic_id}
                label="Topic"
                onChange={(e) => setForm({ ...form, topic_id: e.target.value, subtopic_id: "" })}
              >
                <MenuItem value="">
                  <em>Whole subject (no specific topic)</em>
                </MenuItem>
                {formTax.topics.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth disabled={!form.topic_id || formTax.subtopics.length === 0}>
              <InputLabel>Subtopic</InputLabel>
              <Select
                value={form.subtopic_id}
                label="Subtopic"
                onChange={(e) => setForm({ ...form, subtopic_id: e.target.value })}
              >
                <MenuItem value="">
                  <em>Whole topic (no specific subtopic)</em>
                </MenuItem>
                {formTax.subtopics.map((st) => (
                  <MenuItem key={st.id} value={st.id}>
                    {st.name}
                  </MenuItem>
                ))}
              </Select>
              {!!form.topic_id && formTax.subtopics.length === 0 && (
                <FormHelperText>This topic has no subtopics.</FormHelperText>
              )}
            </FormControl>

            <TextField
              size="small"
              label="Title"
              fullWidth
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <TextField
              size="small"
              label="Description (optional)"
              fullWidth
              multiline
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />

            <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
              {file ? file.name : `Choose file (PDF, PPT, DOC — max ${MAX_MB}MB)`}
              <input
                hidden
                type="file"
                accept=".pdf,.ppt,.pptx,.doc,.docx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Divider />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={submit}
            disabled={saving || !form.course_plan_id || !form.subject_id || !form.title || !file}
          >
            {saving ? "Uploading…" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
